import { Component, Inject, Injectable, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../shared/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable } from 'rxjs';
import { ChangePassword, GoogleRegister, GoogleService, UserFromBE } from '@sameta/shared';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { PasswordResetDialog } from './password-reset/password-reset-dialog';
import { BodyUserDataResponse } from 'src/app/shared/models/responses/body-user-response.model';
import { catchError } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { idToken, signInWithPopup } from '@angular/fire/auth';
import { error } from '@angular/compiler-cli/src/transformers/util';
import { GoogleAuthProvider, getAuth } from "firebase/auth";


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  html: '';
  state$: Observable<any>;

  jobOfferId: number;
  reactionOnJobOffer = false;
  afterEmailVerification = false;
  candidateLogin = false;
  urlPath: string;
  token: string | null;
  verificationHash: string | null;
  loginMsg = '';
  hidePS = true;
  savedIdToken : string


  constructor(
    private fireAuth: AngularFireAuth,
    private fb: FormBuilder,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    public activatedRoute: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    public googleData: GoogleService
  ) {
    this.loginForm = this.fb.group({
      email: ['', Validators.email],
      password: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.jobOfferId = Number(
      this.activatedRoute.snapshot.paramMap.get('jobOfferId')
    );
    this.urlPath = location.pathname.substring(1);

    if (this.jobOfferId || this.urlPath === 'joblogin') {
      this.reactionOnJobOffer = true;
    }


    if (this.authService.afterPasswordReset) {
      this.translate
        .get('login.afterPasswordReset', { value: '' })
        .subscribe((resp: string) => {
          this.snackBar.open(resp, 'CANCEL', {
            duration: 3000,
          });
        });
      this.authService.afterPasswordReset = false;
    }

    //Token is for user verification, verificationHash is for HR verification (to be split into separate login modules in the future)
    this.token = this.activatedRoute.snapshot.queryParamMap.get('token');
    this.verificationHash =
      this.activatedRoute.snapshot.queryParamMap.get('verificationHash');
    if (this.token !== null) {
      this.candidateLogin = true;
      this.verifyEmail(this.token);
    }
    if (this.verificationHash !== null) {
      this.verifyEmail(this.verificationHash);
    }
  }

  checkLoginStatus(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/dashboard']);
    }
  }

  toggleHidePassword(pass: string): void {
    if (pass === 'PS') {
      this.hidePS = !this.hidePS;
    }
  }

  async login(): Promise<void> {
    let idToken: string = ""
    try {
      const dataF = await this.fireAuth.signInWithEmailAndPassword(this.loginForm.get('email')?.value, this.loginForm.get('password')?.value);
      idToken = await dataF.user.getIdToken();
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        catchError(async () => false)
      } else {
        catchError(async () => false)
      }
    }
    this.authService
      .login(idToken)
      .subscribe({
        next: (res) => {
            if (this.candidateLogin) {
              this.router.navigate(['/reg'], {});
            } else {
              this.router.navigate(['/dashboard'], {});
            }
        },
        error: (error) => {
            let cancel;
            let uploadMsg;

            this.translate
              .get('shared.cancel', { value: '' })
              .subscribe((resp: string) => {
                cancel = resp;
              });
            const errorMsg = typeof error === 'string' ? error : (error.error?.message || error.error || error.message);
            if (error == 'USER_NOT_ACTIVE' || errorMsg == 'USER_NOT_ACTIVE') {
              this.translate
                .get('login.twFirstConfirmation', { value: '' })
                .subscribe((resp: string) => {
                  uploadMsg = resp;
                });
            }
            else if (error == 'EMAIL_NOT_VERIFIED' || errorMsg == 'EMAIL_NOT_VERIFIED') {
              this.translate
                .get('login.emailNotVerified', { value: '' })
                .subscribe((resp: string) => {
                  uploadMsg = resp;
                });
            } else {
              this.translate
                .get('login.unauthorized', { value: '' })
                .subscribe((resp: string) => {
                  uploadMsg = resp;
                });
            }
            this.snackBar.open(`${uploadMsg}`, cancel, {
              duration: 4000,
              panelClass: ['snackbar-formater'],
            });
          }
      });
  }


  async joblogin(): Promise<void> {
    let idToken: string = ""
    try {
      const dataF = await this.fireAuth.signInWithEmailAndPassword(this.loginForm.get('email')?.value, this.loginForm.get('password')?.value);
      idToken = await dataF.user.getIdToken();
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        catchError(async () => false)
      } else {
        catchError(async () => false)
      }
    }
    this.authService
      .login(idToken)
      .subscribe({
        next:(res) =>{
          if (this.jobOfferId) {
            this.router.navigate(['contact-form/', this.jobOfferId]);
          }else if (this.urlPath === 'joblogin') {
            this.router.navigate(['job-offers/']);
          }
        },
        error:(error) =>{
          let cancel;
          let uploadMsg;
          this.translate
            .get('login.unauthorized', { value: '' })
            .subscribe((uploadMsg: string) => {
              this.snackBar.open(uploadMsg, cancel, {
                duration: 4000,
                panelClass: ['snackbar-formater'],
              });
            });
        }
      });
  }

  jobregister(): void {
    this.router.navigate(['jobregistration', this.jobOfferId]);
  }

  forgotPassword(): void {
    const dialogRef = this.dialog.open(PasswordResetDialog, {
      autoFocus: false,
    });
  }

  verifyEmail(token: string): void {
    this.authService.verifyEmail(token).subscribe(
      (res) => this.handleVerificationResponse(res),
      (error) => this.handleVerificationError(error)
    );
  }

  handleVerificationResponse(res: BodyUserDataResponse): void {
    this.translate
      .get('shared.emailVerified', { value: '' })
      .subscribe((resp: string) => {
        this.loginMsg = resp;
        this.snackBar.open(resp, 'CANCEL', {
          duration: 3000,
        });
      });
    this.afterEmailVerification = true;
    this.checkLoginStatus();
  }

  googleAuth = async (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    const auth = getAuth();
    signInWithPopup(auth, provider).then(
      (result)=> {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const user = result.user;
        let newUser  = new GoogleRegister();
        newUser.isGoogle = true;
        newUser.name = user.displayName;
        newUser.email = user.email;
        newUser.emailVerified = user.emailVerified;
        newUser.uid = user.uid;
        if (user.emailVerified == true) {
          user.getIdToken().then(idToken => {
            this.savedIdToken = idToken;
            this.googleData.sendGoogleUserData(newUser);

            this.authService.googleRegister(newUser).subscribe({
              next: (res) => {
                if (res === "NOT_REGISTERED") {
                  this.router.navigate(['register']);
                } else if (res === "REGISTERED")  {
                  this.authService
                    .login(idToken)
                    .subscribe({
                      next: (res) => {
                        if (this.candidateLogin) {
                          this.router.navigate(['/reg'], {});
                        } else {
                          this.router.navigate(['/dashboard'], {});
                        }
                      },
                      error: (error) => {
                        let cancel;
                        let uploadMsg;

                        this.translate
                          .get('shared.cancel', { value: '' })
                          .subscribe((resp: string) => {
                            cancel = resp;
                          });
                        const errorMsg = typeof error === 'string' ? error : (error.error?.message || error.error || error.message);
            if (error == 'USER_NOT_ACTIVE' || errorMsg == 'USER_NOT_ACTIVE') {
                          this.translate
                            .get('login.twFirstConfirmation', { value: '' })
                            .subscribe((resp: string) => {
                              uploadMsg = resp;
                            });
                        }
                        else if (error == 'EMAIL_NOT_VERIFIED' || errorMsg == 'EMAIL_NOT_VERIFIED') {
                          this.translate
                            .get('login.emailNotVerified', { value: '' })
                            .subscribe((resp: string) => {
                              uploadMsg = resp;
                            });
                        } else {
                          this.translate
                            .get('login.unauthorized', { value: '' })
                            .subscribe((resp: string) => {
                              uploadMsg = resp;
                            });
                        }
                        this.snackBar.open(`${uploadMsg}`, cancel, {
                          duration: 4000,
                          panelClass: ['snackbar-formater'],
                        });
                      }
                })}
              },
              error: (error) => {
                const errorCode = error.code;
                const errorMessage = error.message
              }
            });
          });
        }
      }
    ).catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message
    })
  }

  handleVerificationError(error: string): void {
    if (error === 'Email Already Verified') {
      this.translate
        .get('login.verificationLinkUsedAlready', { value: '' })
        .subscribe((resp: string) => {
          this.snackBar.open(resp, 'CANCEL', {
            duration: 3000,
          });
        });
    } else {
      this.translate
        .get('login.linkInvalid', { value: '' })
        .subscribe((resp: string) => {
          this.snackBar.open(resp, 'CANCEL', {
            duration: 3000,
          });
        });
    }
    this.afterEmailVerification = true;
    this.checkLoginStatus();
  }
}
